import { Line, ScanFields as ScanFieldsType } from '../types';
import { useValidateSerialNumber } from '../hooks/useValidateSerialNumber';
import { useCreateLocator } from '../hooks/useCreateLocator';
import { useContext } from 'react';
import { UserContext } from '../context/UserContext';

interface SerialValidationResult {
  whid: string;
  organizationId: string;
  inventoryItemId: string;
  serialNumber: string;
  itemNumber: string;
  description: string;
  assetNumber: string;
  serialStatus: string;
  snRepairEligible: string;
}

interface SerialNumberScannerProps {
  lines: Line[];
  scanFields: ScanFieldsType;
  onLinesUpdate: (lines: Line[]) => void;
  onScanFieldsUpdate: (fields: ScanFieldsType) => void;
  onSerialNotFound?: (serialNumber: string) => void;
  onMultipleSerialResults?: (results: SerialValidationResult[]) => void;
  selectedHeaderFromSite?: string;
  selectedHeaderToOrg?: string;
  organizationName?: string;
  onLoadingChange?: (loading: boolean) => void;
  onShowWarning?: (message: string) => void;
}

export const useSerialNumberScanner = ({
  lines,
  scanFields,
  onLinesUpdate,
  onScanFieldsUpdate,
  onSerialNotFound,
  onMultipleSerialResults,
  selectedHeaderFromSite,
  selectedHeaderToOrg,
  organizationName,
  onLoadingChange,
  onShowWarning
}: SerialNumberScannerProps) => {
  
  const { validateSerial } = useValidateSerialNumber();
  const { createLocator } = useCreateLocator();
  const { userId } = useContext(UserContext);
  let currentScannedSerial = '';
  
  // Helper function to match serial numbers with/without leading 'S'
  const matchesSerial = (scannedSerial: string, lineSerial: string): boolean => {
    const normalizeSerial = (serial: string) => serial.toLowerCase().replace(/^s/, '');
    const normalizedScanned = normalizeSerial(scannedSerial);
    const normalizedLine = normalizeSerial(lineSerial);
    console.log('matchesSerial - scanned:', scannedSerial, '→', normalizedScanned);
    console.log('matchesSerial - line:', lineSerial, '→', normalizedLine);
    console.log('matchesSerial - result:', normalizedScanned === normalizedLine);
    return normalizedScanned === normalizedLine;
  };
  
  // Helper function to find line by serial number (with S prefix matching)
  const findLineBySerial = (serialNumber: string): number => {
    console.log('findLineBySerial - searching for:', serialNumber);
    console.log('findLineBySerial - available lines:', lines.map(l => l.serialNumber));
    const index = lines.findIndex(line => {
      const matches = matchesSerial(serialNumber, line.serialNumber || '');
      console.log('findLineBySerial - checking line:', line.serialNumber, 'matches:', matches);
      return matches;
    });
    console.log('findLineBySerial - found index:', index);
    return index;
  };
  
  const processSerialValidation = async (result: SerialValidationResult, matchingLineIndex: number) => {
    console.log('processSerialValidation called with:', result, matchingLineIndex);
    const newLines = [...lines];
    const originalLine = newLines[matchingLineIndex];
    console.log('Original line:', originalLine);
    
    // Create locator if all required fields are present
    let locator = scanFields.toLocator;
    if (scanFields.pallet && scanFields.cage && scanFields.tote && scanFields.toSubinventory && selectedHeaderToOrg && organizationName && userId) {
      try {
        const createdLocator = await createLocator({
          p_user_id: userId,
          p_organization_name: organizationName,
          p_subinventory: scanFields.toSubinventory,
          p_pallet: scanFields.pallet,
          p_cage: scanFields.cage,
          p_tote: scanFields.tote
        });
        if (createdLocator) {
          locator = createdLocator;
          console.log('Created locator:', createdLocator);
        }
      } catch (error) {
        console.error('Failed to create locator:', error);
      }
    }
    
    const matchingLine = {
      ...originalLine,
      validatedSerialNumber: result.serialNumber,
      validatedAssetNumber: result.assetNumber,
      validatedSerialItem: result.itemNumber,
      description: result.description,
      toSubinventory: scanFields.toSubinventory,
      toLocator: locator,
      trackingNumber: scanFields.trackingNumber,
      repairEligibility: "Yes"
    };
    
    console.log('Updated line:', matchingLine);
    
    // Remove the matching line from its current position
    newLines.splice(matchingLineIndex, 1);
    // Add it to the top
    newLines.unshift(matchingLine);
    
    console.log('New lines array:', newLines);
    onLinesUpdate(newLines);
    onScanFieldsUpdate({ ...scanFields, scanSerialNumber: "" });
  };
  
  const scanSerialNumber = async (serialNumber: string) => {
    console.log('scanSerialNumber called with:', serialNumber);
    console.log('selectedHeaderFromSite:', selectedHeaderFromSite);
    console.log('scanFields:', scanFields);
    
    currentScannedSerial = serialNumber; // Store the scanned serial
    
    // Check if serial number exists with Success status or validated serial number
    const successLine = lines.find(line => 
      matchesSerial(serialNumber, line.serialNumber) && 
      (
        (line.status && line.status.toLowerCase() === 'success') ||
        line.validatedSerialNumber
      )
    );
    
    if (successLine) {
      if (onShowWarning) {
        onShowWarning('Serial Number has been already received and processed');
      }
      onScanFieldsUpdate({ ...scanFields, scanSerialNumber: "" });
      return;
    }
    
    if (!scanFields.toSubinventory || !scanFields.trackingNumber) {
      console.log('Missing mandatory fields');
      return;
    }

    onLoadingChange?.(true);
    
    // Always call validate-sn API first, regardless of whether serial exists in lines
    try {
      console.log('Calling validateSerial API');
      const results = await validateSerial(selectedHeaderFromSite || '', serialNumber);
      console.log('API results:', results);
      
      // Check if all results are "Not Eligible"
      const allNotEligible = results.length > 0 && results.every(result => result.snRepairEligible === "Not Eligible");
      
      if (results.length === 0) {
        // No response - check if serial exists in lines table
        const matchingLineIndex = findLineBySerial(serialNumber);
        if (matchingLineIndex !== -1) {
          // Serial exists in lines table - open Serial Exception form with lines table serial number
          if (onSerialNotFound) {
            onSerialNotFound(lines[matchingLineIndex].serialNumber);
          }
        } else {
          // Serial doesn't exist in lines table - open Serial Exception form with scanned serial
          if (onSerialNotFound) {
            onSerialNotFound(serialNumber);
          }
        }
      } else if (results.length === 1 && results[0].snRepairEligible === "Not Eligible") {
        // Single "Not Eligible" result - check if serial exists in lines
        const matchingLineIndex = findLineBySerial(serialNumber);
        if (matchingLineIndex !== -1) {
          // Update existing line with "No" repair eligibility
          const updatedResult = { ...results[0], snRepairEligible: "No" };
          processSerialValidation(updatedResult, matchingLineIndex);
        } else {
          // No matching line - check if serial exists in lines table before opening form
          const serialExistsInLines = lines.some(line => matchesSerial(serialNumber, line.serialNumber));
          if (!serialExistsInLines) {
            if (onSerialNotFound) {
              onSerialNotFound(serialNumber);
            }
          }
        }
      } else if (allNotEligible && results.length > 1) {
        // Multiple "Not Eligible" results - show popup
        if (onMultipleSerialResults) {
          onMultipleSerialResults(results);
        }
      } else if (results.length === 1) {
        // Check if serial exists in lines table for single result
        const matchingLineIndex = findLineBySerial(serialNumber);
        if (matchingLineIndex !== -1) {
          processSerialValidation(results[0], matchingLineIndex);
        }
      } else if (results.length > 1) {
        // Multiple results - show popup
        if (onMultipleSerialResults) {
          onMultipleSerialResults(results);
        }
      } else {
        // No response - should not reach here but fallback
        if (onSerialNotFound) {
          onSerialNotFound(serialNumber);
        }
      }
    } catch (error) {
      console.error('Error validating serial number:', error);
    } finally {
      onLoadingChange?.(false);
    }
  };

  const processPopupSelection = async (selectedResult: SerialValidationResult) => {
    console.log('processPopupSelection called with:', selectedResult);
    console.log('currentScannedSerial:', currentScannedSerial);
    console.log('lines:', lines);
    
    // Check if the selected serial number exists in lines table
    const matchingLineIndex = findLineBySerial(selectedResult.serialNumber);
    
    console.log('matchingLineIndex:', matchingLineIndex);
    
    if (matchingLineIndex !== -1) {
      // Update existing line
      console.log('Updating existing line');
      await processSerialValidation(selectedResult, matchingLineIndex);
    } else {
      // Create locator for new line if all required fields are present
      let locator = scanFields.toLocator;
      if (scanFields.pallet && scanFields.cage && scanFields.tote && scanFields.toSubinventory && selectedHeaderToOrg && organizationName && userId) {
        try {
          const createdLocator = await createLocator({
            p_user_id: userId,
            p_organization_name: organizationName,
            p_subinventory: scanFields.toSubinventory,
            p_pallet: scanFields.pallet,
            p_cage: scanFields.cage,
            p_tote: scanFields.tote
          });
          if (createdLocator) {
            locator = createdLocator;
            console.log('Created locator for new line:', createdLocator);
          }
        } catch (error) {
          console.error('Failed to create locator for new line:', error);
        }
      }
      
      // Create new line
      console.log('Creating new line');
      const newLine: Line = {
        id: `new-${Date.now()}`,
        description: selectedResult.description,
        serialNumber: selectedResult.serialNumber,
        assetNumber: selectedResult.assetNumber,
        validatedSerialNumber: selectedResult.serialNumber,
        validatedAssetNumber: selectedResult.assetNumber,
        itemNumber: selectedResult.itemNumber,
        toSubinventory: scanFields.toSubinventory,
        toLocator: locator,
        validatedSerialItem: selectedResult.itemNumber,
        status: '',
        trackingNumber: scanFields.trackingNumber,
        internalSalesOrder: '',
        isoLineNumber: '',
        replacementOrderLineStatus: '',
        repairEligibility: "Yes"
      };
      
      console.log('New line created:', newLine);
      const newLines = [newLine, ...lines];
      console.log('Updated lines array:', newLines);
      onLinesUpdate(newLines);
      onScanFieldsUpdate({ ...scanFields, scanSerialNumber: "" });
    }
  };

  return {
    scanSerialNumber,
    processSerialValidation,
    processPopupSelection
  };
};